/**
 * Elite-tab rebalancing unlock section (attention status + insight card + CTA).
 *
 * Inspired by `NewEliteLandingRebalancingPlanSection` / `RebalancingPlanSection`,
 * restyled to the Elite Plans Figma (needs-attention eyebrow, stacked banner CTA).
 *
 * Belongs here: sell-landing rebalance prompt when funds-to-rebalance > 0.
 * Does not belong here: smart-rebalance timer / member dashboard unlock flows.
 */
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import UpgradeAppContent from '../../../components/BottomSheetComponents/UpgradeAppContent'
import { postCTA as postCta } from '../../../utils/appBridge'
import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { getEliteBuyCtaEventExtras } from '../../../utils/eliteEventExtras'
import { buildFamilyOnboardingViewProps } from '../../new-elite-landing/pages/family/familyOnboardingViewProps'
import type { EliteOnboardingStatusResponse } from '../../../types/misc.types'
import { ELITE_PLANS_ELITE_TAB_ID, useElitePlansLanding } from '../ElitePlansLandingContext'
import { buildElitePlansAdvisoryCtaPayload } from '../elitePlansAdvisorySelection'
import { buildElitePlansCtaPayload } from '../elitePlansLandingState'
import { resolveIsFamilyBlocked } from '../elitePlansMembership'
import { ElitePlansEventId, trackElitePlansClick } from '../analytics'

const INFO_CIRCLE_SRC = '/images/elite-plans/elite/rebalancing/info-circle.svg'
const CARD_CIRCLES_SRC = '/images/elite-plans/elite/rebalancing/card-circles.svg'

const CTA_LABEL = 'Unlock plan with Elite'

/** Prefer the first positive count from top-level status, insights, then context fallback. */
function resolveFundsToRebalanceCount(
  topLevel: number | undefined,
  fromInsights: number | undefined,
  fallback: number
): number {
  if (topLevel != null && topLevel > 0) return topLevel
  if (fromInsights != null && fromInsights > 0) return fromInsights
  if (topLevel != null) return topLevel
  if (fromInsights != null) return fromInsights
  return fallback
}

function resolveMissedGainsAmount(topLevel: number, fromInsights: number | undefined): number {
  if (topLevel > 0) return topLevel
  return fromInsights ?? 0
}

/** Whether the Elite-tab SEBI rebalancing section will mount (gates advice-carousel separator). */
export function shouldShowElitePlansRebalancingPlanSection({
  activeTabId,
  status,
  isStatusLoading,
  fundsToRebalanceFallback
}: {
  activeTabId: string
  status?: EliteOnboardingStatusResponse
  isStatusLoading: boolean
  fundsToRebalanceFallback: number
}): boolean {
  if (isStatusLoading || activeTabId !== ELITE_PLANS_ELITE_TAB_ID) return false
  if (resolveIsFamilyBlocked(status)) return false
  return (
    resolveFundsToRebalanceCount(
      status?.numberOfFundsToRebalance,
      status?.eliteInsights?.numberOfFundsToRebalance,
      fundsToRebalanceFallback
    ) > 0
  )
}

export function ElitePlansRebalancingPlanSection() {
  const {
    activeTab,
    status,
    lifecycle,
    eliteAdvisory,
    isStatusLoading,
    missedGains,
    fundsToRebalanceFallback
  } = useElitePlansLanding()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  const numberOfFundsToRebalance = resolveFundsToRebalanceCount(
    status?.numberOfFundsToRebalance,
    status?.eliteInsights?.numberOfFundsToRebalance,
    fundsToRebalanceFallback
  )
  const displayMissedGains = resolveMissedGainsAmount(
    missedGains,
    status?.eliteInsights?.missedGains
  )
  const isEliteTab = activeTab.id === ELITE_PLANS_ELITE_TAB_ID
  const isFamilyBlocked = resolveIsFamilyBlocked(status)
  const familyConfig = buildFamilyOnboardingViewProps(status)
  const usesAdvisoryFlow = isEliteTab && eliteAdvisory !== null
  const isCtaDisabledByState = usesAdvisoryFlow
    ? eliteAdvisory.isCtaDisabledByState
    : lifecycle.isCtaDisabledByState
  const isForceUpdateRequired = status?.isForceUpdateRequired ?? false
  const isCtaDisabled = isCtaDisabledByState || isFamilyBlocked

  if (
    !shouldShowElitePlansRebalancingPlanSection({
      activeTabId: activeTab.id,
      status,
      isStatusLoading,
      fundsToRebalanceFallback
    })
  ) {
    return null
  }

  const portfolioOwnerLabel =
    familyConfig.isFamilyMode && familyConfig.secondaryUserName
      ? `${familyConfig.secondaryUserName}'s`
      : 'your'
  const readyBannerLabel =
    familyConfig.isFamilyMode && familyConfig.secondaryUserName
      ? `${familyConfig.secondaryUserName}'s personalised rebalancing plan is ready`
      : 'Your personalised rebalancing plan is ready'
  const fundWord = numberOfFundsToRebalance === 1 ? 'fund' : 'funds'

  const handleCtaActivate = () => {
    trackElitePlansClick(ElitePlansEventId.CtaClick, {
      ...getEliteBuyCtaEventExtras(status),
      planId: activeTab.id,
      buttonLabel: CTA_LABEL,
      scrollSection: 'rebalancing_plan'
    })
    if (isCtaDisabled) return
    if (isForceUpdateRequired) {
      openBottomSheet(<UpgradeAppContent onDismiss={closeBottomSheet} />)
      return
    }
    const payload = usesAdvisoryFlow
      ? buildElitePlansAdvisoryCtaPayload(
        status,
        eliteAdvisory.liveCta,
        eliteAdvisory.selectedTenure
      )
      : buildElitePlansCtaPayload(status, lifecycle.liveCta)
    if (!payload) return
    postCta(JSON.stringify(payload))
  }

  return (
    <section className='mx-auto flex w-full max-w-[720px] flex-col items-center gap-6 px-5'>
      <div className='flex w-full flex-col items-center gap-3.5'>
        <p className='m-0 flex items-center gap-1.5 text-[13px] font-medium leading-[1.4] text-[#cc6e30]'>
          <img
            src={INFO_CIRCLE_SRC}
            alt=''
            aria-hidden='true'
            width={14}
            height={14}
            className='size-3.5 shrink-0'
          />
          Needs your attention
        </p>
        <p className='m-0 max-w-[297px] text-center text-[18px] font-normal leading-[1.4] text-[#232323]'>
          SEBI-registered experts analysed {portfolioOwnerLabel} portfolio and found:
        </p>
      </div>

      <div
        className='relative h-[82px] w-full overflow-hidden rounded-[15px] border-[1.5px] border-solid border-[rgba(222,205,182,0.3)]'
        style={{
          backgroundImage:
            'linear-gradient(162deg, rgba(255, 240, 219, 0.3) 3.65%, rgb(255, 255, 255) 34.93%)'
        }}
      >
        <img
          src={CARD_CIRCLES_SRC}
          alt=''
          aria-hidden='true'
          className='pointer-events-none absolute -right-10 -top-0.5 h-[254px] w-[312px] opacity-50'
        />
        <div className='relative z-[1] flex flex-col gap-0.5 px-[18px] pt-3.5 text-left text-base font-medium leading-[1.5]'>
          <p className='m-0 text-[#232323]'>
            {numberOfFundsToRebalance} {fundWord} to rebalance
          </p>
          {displayMissedGains > 0 ? (
            <p className='m-0 text-[#cc6e30]'>
              {roundOffAndFormatNumberWithCurrency(displayMissedGains)} missed gains
            </p>
          ) : null}
        </div>
      </div>

      <div className='flex w-full flex-col gap-3'>
        <div className='relative'>
          <div
            className='rounded-xl px-2 pb-16 pt-1.5 text-center text-[13px] font-medium leading-[1.5] text-[#232323]'
            style={{
              backgroundImage:
                'linear-gradient(96deg, rgb(244, 218, 198) 57%, rgb(255, 245, 237) 168%)'
            }}
          >
            {readyBannerLabel}
          </div>
          <button
            type='button'
            onClick={handleCtaActivate}
            disabled={isCtaDisabled}
            className={`ep-focus-ring absolute inset-x-0 bottom-0 flex h-14 w-full items-center justify-center rounded-xl border-none px-6 text-base font-medium text-white ${isCtaDisabled ? 'bg-[#d1d6d6]' : 'bg-[#232323]'
              }`}
          >
            {CTA_LABEL}
          </button>
        </div>
        <p className='m-0 text-center text-[13px] font-normal leading-[1.5] text-[#6a6e6e]'>
          Know which funds to exit and switch to better performing ones, with {portfolioOwnerLabel}{' '}
          rebalancing plan
        </p>
      </div>
    </section>
  )
}
